import React, { useState } from 'react';
import { useStacklineTheme } from '@stackline/ui';
import ReactDOMServer from 'react-dom/server';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import {
  useApplyEntityFilters,
  useRemoveRetailPriceRangeFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import {
  STAR_RATING_RANGE,
  parseCumulativeProductCatalogCountRatingResponse,
  useLatestCumulativeReviewWeekId
} from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewsHooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { GenericXAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/GenericXAxisLabel';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { useAppSelector } from 'src/utils/Hooks';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';
import { saveSingleBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';

const productCatalogCountOptions = [
  { label: 'Product Catalog Count by Cumulative', id: 'cumulativeProductCatalogCount' }
];

export const useLifetimeProductCatalogCountReviewRatingData = () => {
  const retailer = useAppSelector((state) => state.retailer);
  const beaconClientId = useAppSelector((state) => state.user.config.vendor.BeaconClientId);

  const { endWeek: comparisonEndWeek } = useAppSelector((state) => state.comparisonTimePeriod);

  const { latestCumulativeWeekId } = useLatestCumulativeReviewWeekId();

  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters('reviews');

  const requestArray = STAR_RATING_RANGE.map(({ range: [min, max] }) => {
    const aggregationByLifeTimeReviewRating = new AggregationBuilder('retailerId')
      .addAggregationField('Stackline SKU', 'stacklineSku', 'cardinality', false)
      .setSortDirection(null)
      .setGroupByFieldName('retailerId')
      .addConditionRangeFilter('weekId', latestCumulativeWeekId, latestCumulativeWeekId)
      .addComparisonRangeFilter('weekId', comparisonEndWeek, comparisonEndWeek)
      .build();

    const requestByLifetimeReviewRating = new AdvancedSearchRequestBuilder(
      `mainEntityMetrics-client-${beaconClientId}-productCatalogCountReviewRatingData`,
      'beacon-ratings-reviews-metrics-v2'
    )
      .setPageNumber(1)
      .setPageSize(10000)
      .setReturnAdditionalMetadata(false)
      .setRetailerId(retailer.id)
      .setPeriod('year')
      .setDoAggregation(true)
      .setReturnDocuments(false)
      .addConditionTermFilter('retailerId', 'should', [retailer.id])
      .setSearchBy('parent')
      .addAggregation(aggregationByLifeTimeReviewRating)
      .addSortField('Stackline SKU', 'stacklineSku', 'cardinality', false)
      .apply(applyEntityFilters)
      .apply(removeRetailPriceRangeFilters);

    requestByLifetimeReviewRating.addConditionRangeFilter('lifetimeReviewAverageRating', min, max);
    return requestByLifetimeReviewRating.build();
  });

  const { isLoading, data } = useGenericAdvancedSearch({
    requestId: 'productCatalogCountReviewRating-columnChart',
    requestBody: requestArray,
    queryKeys: requestArray
  });

  return { isLoading, data };
};

/**
 *
 * Product Catalog Count by Cumulative Review Rating Tier in Review (Analysis) page.
 */

const ProductCatalogCountReviewRatingChart = () => {
  const theme = useStacklineTheme();
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const [selectedMetric, setSelectedMetric] = useState('cumulativeProductCatalogCount');
  const normalizedSelectedMetric = productCatalogCountOptions.some((option) => option.id === selectedMetric)
    ? selectedMetric
    : 'cumulativeProductCatalogCount';
  const shouldShowMetricDropdown = productCatalogCountOptions.length > 1;
  const selectedMetricLabel =
    productCatalogCountOptions.find((option) => option.id === normalizedSelectedMetric)?.label ||
    productCatalogCountOptions[0].label;
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const { endWeek: comparisonEndWeek } = useAppSelector((state) => state.comparisonTimePeriod);

  const { data: cumulativeData, isLoading: cumulativeLoading } = useLifetimeProductCatalogCountReviewRatingData();

  const { currentData } = cumulativeData
    ? parseCumulativeProductCatalogCountRatingResponse({
        data: cumulativeData as any,
        comparisonEndWeek
      })
    : { currentData: [] };
  const chartData = currentData as [string, number][];

  return (
    <BeaconChartWithLegend
      title={`${selectedMetricLabel} Review Rating Tier`}
      includeSummaryInfo={false}
      convertSeriesToDelimitedData={async () => {
        return saveSingleBarChartAsDelimitedData(chartData, { metricLabel: 'Product Catalog Count' });
      }}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the number of products for ${entityPhrase} `}
            {taxonomyText} in each review rating tier.
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      singleDropdownProps={
        shouldShowMetricDropdown
          ? {
              defaultLabel: '',
              options: productCatalogCountOptions,
              selectedId: normalizedSelectedMetric,
              onChange: (option) => setSelectedMetric(option.id),
              suffix: 'Review Rating Tier'
            }
          : undefined
      }
      primaryLegendProps={{
        displayName: legendLabel
      }}
      loading={cumulativeLoading}
      loadingComponent={<TopEntityChartV2Loading enableSubTitleSkeleton />}
    >
      <TopEntityChartV2
        xAxisLabelTextStyles={{ lineHeight: 1.6 }}
        seriesColors={[theme.colors.primary]}
        xAxisFormatter={(xAxisCategory) => {
          return ReactDOMServer.renderToStaticMarkup(
            <GenericXAxisLabel xAxisCategory={xAxisCategory} wrapperStyle={{ width: '50px' }} />
          );
        }}
        chartSeries={[
          {
            type: 'column',
            data: chartData,
            metricType: METRICTYPE.VOLUME
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};

export default ProductCatalogCountReviewRatingChart;
